/**
 * Shape-agnostic helpers for rendering arbitrary JSON payloads.
 * No knowledge of any particular payload lives here — callers supply the paths.
 */

type JsonContainer = Record<string, unknown> | unknown[];

export function isJsonContainer(value: unknown): value is JsonContainer {
  return typeof value === 'object' && value !== null;
}

/** True when the value should be walked as a nested group rather than printed inline. */
export function isJsonBranch(value: unknown): boolean {
  if (!isJsonContainer(value)) return false;

  const children = Object.values(value);
  if (children.length === 0) return false;

  // * an array of scalars reads better as a single comma-separated line
  return !(
    Array.isArray(value) && children.every((child) => !isJsonContainer(child))
  );
}

/** True when there is something worth showing — empty objects/arrays/strings count as nothing. */
export function hasJsonContent(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (isJsonContainer(value)) return Object.keys(value).length > 0;

  return true;
}

const INLINE_LIST_MAX_ITEMS = 3;
const INLINE_LIST_MAX_LENGTH = 80;

/** Short lists read better inline; longer ones get a line each (and so a block). */
function formatScalarList(items: string[]): string {
  const inline = items.join(', ');

  return items.length <= INLINE_LIST_MAX_ITEMS &&
    inline.length <= INLINE_LIST_MAX_LENGTH
    ? inline
    : items.join('\n');
}

export function formatJsonValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return '';

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';

    return value.every((item) => !isJsonContainer(item))
      ? formatScalarList(value.map((item) => String(item)))
      : JSON.stringify(value, null, 2);
  }

  if (isJsonContainer(value)) {
    return Object.keys(value).length === 0
      ? '{}'
      : JSON.stringify(value, null, 2);
  }

  return String(value);
}

/** Resolves a dotted path (`ext_log.record.http_error_message`) against a payload. */
export function resolveJsonPath(source: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (current, segment) =>
        isJsonContainer(current) ? current[segment] : undefined,
      source,
    );
}
